'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import FocusTrap from 'focus-trap-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRef } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { StickyScrollContainer } from 'react-unstuck';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { fade } from '../../animations/fade.js';
import { slide } from '../../animations/slide.js';
import { zIndexUtil } from '../../helpers/zIndexUtil.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { scrollDirection } from '../../mixins/scrollDirection.js';
import { Dimmer } from '../Dimmer/Dimmer.js';
import { Portal } from '../Portal/Portal.js';
import { DrawerProvider } from './DrawerContext.js';

const StyledDrawer = styled(motion.div).withConfig({
  shouldForwardProp: prop => !['width', 'zIndex'].includes(prop)
}).withConfig({
  componentId: "sc-xxofvg-0"
})(["top:0;max-width:", ";width:", ";z-index:", ";height:100%;outline:none;position:fixed;overflow:hidden;", " ", ""], ({
  width
}) => width === 'auto' || width === '100%' ? '100%' : `${width}px`, ({
  width
}) => width !== 'auto' ? '100%' : width, ({
  zIndex
}) => zIndex, switchProp('variant', {
  primary: css(["background-color:", ";box-shadow:", ";"], ({
    theme
  }) => theme.components.drawer.backgroundColor, ({
    theme
  }) => theme.components.drawer.boxShadow),
  secondary: css(["background-color:", ";box-shadow:", ";"], ({
    theme
  }) => theme.components.drawer.backgroundColorSecondary, ({
    theme
  }) => theme.components.drawer.boxShadowSecondary)
}), switchProp('position', {
  left: 'left: 0;',
  bottom: 'left: 0;',
  top: 'left: 0;',
  right: 'right: 0;'
}));
const StyledScroller = styled.div.withConfig({
  componentId: "sc-xxofvg-1"
})(["position:relative;height:inherit;", ";"], ({
  hasScroll
}) => hasScroll && scrollDirection.vertical());
const Drawer = /*#__PURE__*/withTitan(({
  isShown,
  children,
  onClose,
  duration = 200,
  animationType = 'slide',
  zIndex = zIndexUtil.drawer,
  position = 'left',
  width,
  variant = 'primary',
  hasScrollLock = true,
  hasScroll = true,
  testId = 'drawer'
}, ref) => {
  const drawerRef = useRef(null);
  const getAnimation = () => {
    if (animationType === 'slide') {
      return slide[position];
    }
    return fade.in;
  };
  return jsx(DrawerProvider, {
    close: onClose,
    children: jsx(AnimatePresence, {
      children: isShown && jsx(Portal, {
        children: jsxs(RemoveScroll, {
          className: "scroll",
          enabled: hasScrollLock,
          children: [jsx(FocusTrap, {
            focusTrapOptions: {
              clickOutsideDeactivates: true,
              fallbackFocus: 'body'
            },
            children: jsx(StyledDrawer, Object.assign({
              "aria-modal": "true",
              "data-test-id": testId,
              position: position,
              role: "dialog",
              transition: {
                ease: 'easeOut',
                duration: duration / 1000
              },
              variant: variant,
              width: width,
              zIndex: zIndex
            }, getAnimation(), {
              ref: drawerRef,
              children: jsx(StickyScrollContainer, {
                element: jsx(StyledScroller, {
                  ref: ref,
                  hasScroll: hasScroll
                }),
                style: {
                  height: 'inherit'
                },
                children: children
              })
            }), "drawer")
          }), jsx(Dimmer, Object.assign({
            zIndex: zIndex - 10,
            onClose: onClose
          }, fade.in), "dimmer")]
        })
      })
    })
  });
}, {
  name: 'Drawer'
});

export { Drawer };
//# sourceMappingURL=Drawer.js.map
